import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import {
	DocumentGetLessonPlanRoot,
	DocumentListFolder,
	DocumentUploadFile,
	DocumentDeleteFolder,
	DocumentDownloadFile,
} from 'services/documentService';
import { Modal, Button, Table, Upload, message, Space } from 'antd';
import {
	FileOutlined,
	DeleteOutlined,
	UploadOutlined,
	DownloadOutlined,
} from '@ant-design/icons';
import ErrorModal from 'popup/ErrorModal';
import { useConfirm } from 'popup/ConfirmContext';
import { DocumentItem } from 'models/model';
import { useParams } from 'react-router-dom';

// @ts-ignore
export default function LessonPlanPage() {
	const { licenseClassId }: any = useParams();
	const { confirm } = useConfirm();
	const [documents, setDocuments] = useState<DocumentItem[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [errorModalOpen, setErrorModalOpen] = useState(false);
	const [uploadModalVisible, setUploadModalVisible] = useState(false);
	const [uploadType, setUploadType] = useState<'cover' | 'plan'>('cover');
	const [rootFolder, setRootFolder] = useState<DocumentItem | null>(null);

	// Load lesson plan data
	useEffect(() => {
		loadLessonPlan();
	}, [licenseClassId]);

	const loadLessonPlan = async () => {
		try {
			setLoading(true);
			const response = await DocumentGetLessonPlanRoot(licenseClassId);

			if (response.result) {
				const rootFolder = response.result;
				setRootFolder(rootFolder.url);
				const listResponse = await DocumentListFolder({
					courseId: licenseClassId,
					path: rootFolder.url,
				});

				if (listResponse.result) {
					setDocuments(listResponse.result);
				}
			}
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi tải giáo án';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setLoading(false);
		}
	};

	const handleDownload = async (item: any) => {
		try {
			setLoading(true);
			const blob = await DocumentDownloadFile({
				filePath: item.url,
			});

			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = item.name;
			document.body.appendChild(a);
			a.click();
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi tải xuống';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setLoading(false);
		}
	};

	const handleUpload = async (options: any) => {
		const { file, onSuccess, onError } = options;
		const fileName =
			uploadType === 'cover' ? 'BiaGiaoAn.docx' : 'GiaoAn.docx';

		try {
			const response = await DocumentUploadFile({
				courseId: licenseClassId,
				name: fileName,
				targetPath: rootFolder,
				file,
			});

			if (response.result) {
				onSuccess(response, file);
				message.success(`Tải lên ${fileName} thành công`);
				await loadLessonPlan();
				setUploadModalVisible(false);
			} else {
				onError(new Error(response.message || 'Tải lên thất bại'));
			}
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi';
			setErrorMessage(errorMsg);
			setUploadModalVisible(false);
			setErrorModalOpen(true);
		}
	};

	const handleDelete = async (item: DocumentItem) => {
		const isConfirmed = await confirm({
			title: 'Xác nhận xóa',
			description: `Bạn có chắc chắn muốn xóa giáo án này?`,
		});

		if (!isConfirmed) return;

		try {
			setLoading(true);
			const response = await DocumentDeleteFolder({
				courseId: licenseClassId,
				itemPath: item.url,
			});

			if (response.result) {
				toast.success('Xóa giáo án thành công');
				await loadLessonPlan();
			}
		} catch (error: any) {
			const errorMsg =
				error?.responseException?.exceptionMessage ||
				error?.message ||
				'Lỗi khi xóa giáo án';
			setErrorMessage(errorMsg);
			setErrorModalOpen(true);
		} finally {
			setLoading(false);
		}
	};

	const columns = [
		{
			title: 'Tên giáo án',
			dataIndex: 'name',
			key: 'name',
			render: (text: string) => (
				<div style={{ display: 'flex', alignItems: 'center' }}>
					<FileOutlined style={{ fontSize: 18 }} />
					<span style={{ marginLeft: 8 }}>{text}</span>
				</div>
			),
		},
		{
			title: 'Ngày sửa',
			dataIndex: 'updateDate',
			key: 'updateDate',
			width: 200,
			render: (date: string) => new Date(date).toLocaleString(),
		},
		{
			title: 'Thao tác',
			key: 'action',
			width: 100,
			render: (_: any, record: DocumentItem) => (
				<Space>
					<Button
						type='text'
						icon={<DownloadOutlined />}
						onClick={() => handleDownload(record)}
					/>
					<Button
						type='text'
						icon={<DeleteOutlined />}
						danger
						onClick={() => handleDelete(record)}
					/>
				</Space>
			),
		},
	];

	return (
		<div className='p-6'>
			<div className='flex justify-end mb-4'>
				<Button
					type='primary'
					onClick={() => setUploadModalVisible(true)}
					icon={<UploadOutlined />}>
					Tải giáo án lên
				</Button>
			</div>

			<Table
				columns={columns}
				dataSource={documents}
				rowKey='url'
				loading={loading}
				pagination={false}
				locale={{ emptyText: 'Chưa có giáo án nào được tải lên.' }}
			/>

			{/* Upload Modal */}
			<Modal
				title='Tải mẫu lên'
				open={uploadModalVisible}
				onCancel={() => setUploadModalVisible(false)}
				footer={null}
				centered>
				<div className='flex flex-col gap-4 py-5'>
					<Upload
						customRequest={handleUpload}
						showUploadList={false}
						beforeUpload={() => {
							setUploadType('cover');
							return true;
						}}
						accept='.doc,.docx'>
						<Button type='primary' block size='large'>
							Tải bìa giáo án (BiaGiaoAn.docx)
						</Button>
					</Upload>

					<Upload
						customRequest={handleUpload}
						showUploadList={false}
						beforeUpload={() => {
							setUploadType('plan');
							return true;
						}}
						accept='.doc,.docx'>
						<Button type='primary' block size='large'>
							Tải giáo án (GiaoAn.docx)
						</Button>
					</Upload>
				</div>
			</Modal>

			<ErrorModal
				isOpen={errorModalOpen}
				message={errorMessage}
				onClose={() => setErrorModalOpen(false)}
			/>
		</div>
	);
}
